import Decimal from "decimal.js";
import { FuzzyDate } from "../system";
import { Size } from "../drawing";
import { IHeightAutoAdjustable } from "../document/items";

enum RoundKinds {
  // 四捨五入
  HalfUp = 1,

  // 切り上げ
  Up = 2,

  // 切り捨て
  Down = 3,
}

// [テキスト自動縮小機能]
// 現在の文字サイズとエリアの幅で、1行あたりの入力できる文字数が算出できるので、
// 入力された文字列を改行コードで分割、全角なら１、半角なら0.5でどの時点で折り返しが発生するかを計算。
// これでトータルした行数が求められ、LineHight* フォントサイズを1行あたりの高さとし、トータルの高さが現在の入力エリアを超えたら、フォントサイズを0.1ずつ落としていきます。
// 入りきった、または最小サイズに達するまでリサイズしていっているようです。
// 単一行指定の時はline-heightを考慮しない模様
// http://git.pscad.co.jp:8080/gitbucket/documakerGov/WebEditor/blob/8a67bb57a756f2dd92e8948b373821ea0689170f/DocuMakerWebEditor/WebContent/jp/docuMaker/editor/js/ReportEditScreen.js#L1867
export class FontSizeCalculator {
  public static calculate(isMultiline: boolean, size: Size, baseFontSize: number, lineHeight: number, value: any): number {
    try {
      return isMultiline
        ? this.getAutoFontSize(size.width, size.height, baseFontSize, lineHeight, this.toText(value))
        : this.getAutoFontSizeTextBox(size.width, size.height, baseFontSize, this.toText(value));
    } catch (e) {
      console.warn(e);
      console.warn(value);
      return baseFontSize;
    }
  }

  private static toText(value: any): string {
    if (typeof value === "string") { return value; }
    if (typeof value === "number") { return value.toString(); }
    if ((value instanceof FuzzyDate) && (typeof value.value === "string")) { return value.value; }
    // FuzzyDateのフォーマット後の計算はここでは対応しない（フォーマット情報が無いので、ここに来る前にstringにしている）
    return null;
  }

  private static getAutoFontSize(width: number, height: number, fontSize: number, lineHeight: number, text: string): number {
    if (!text) { return fontSize; }
    const displayLineCount = this.calculateDisplayLineCount(width, fontSize, text);
    const totalHeight = new Decimal(displayLineCount).mul(fontSize).mul(lineHeight).toNumber();
    if (height >= totalHeight) { return fontSize; }
    const newFontSize = this.getAutoFontSize(width, height, new Decimal(fontSize).sub(0.1).toNumber(), lineHeight, text);
    return this.roundSpecificDecimalPlace(newFontSize, 2, RoundKinds.HalfUp);
  }

  private static calculateDisplayLineCount(width: number, fontSize: number, text: string): number {
    if (!text) { return 1; }
    // 2021年時点ではぴったりの横幅だと折り返してしまう現象が発生していた模様
    // #4419で横幅から1を引く修正をして対応している
    // 2024年になって、逆に可変処理で余計に伸ばしてしまう現象があり、上記修正が関係していることが判明したため再現確認してみたところ、#4419をリバートしても再現しなかった
    // #4419で修正する直前のモジュールでは再現したので、2024年までの何かの修正により再現しなくなっている
    // #4609でtransform経由で描画するよう修正していて、その影響で現象が発生しなくなったものと思われる
    // 可変しすぎてしまう現象の修正のため、#4419で横幅から1を引く修正を差し戻ししている
    const maxLength = this.roundSpecificDecimalPlace(new Decimal(width).div(fontSize).toNumber(), 2, RoundKinds.Down);
    let displayLineCount = 0;

    const lines = text.split("\n");
    for (const line of lines) {
      const lineLength = this.strLength(line);
      if (lineLength <= maxLength) {
        displayLineCount++;
      } else {
        let checkingText = line;
        let chkCnt = lineLength;
        while (chkCnt > 0) {
          // 自動で折り返されるぶんも計算して最終的な行数を算出している模様
          const setText = this.getHeadText(checkingText, maxLength);
          checkingText = checkingText.substring(setText.length);
          displayLineCount++;
          if (!checkingText || checkingText === "") { break; }

          // おそらく極小のオブジェクトに対する無限ループ対策（V1WebDocuからの移植漏れ対応）
          if (!setText || setText === "") { break; }

          // 無限ループ対策？
          chkCnt = new Decimal(chkCnt).sub(0.5).toNumber();
        }
      }
    }
    return displayLineCount;
  }

  private static getAutoFontSizeTextBox(width: number, height: number, fontSize: number, text: string): number {
    if (!text) {
      return fontSize;
    }
    // まず高さを確認する
    const maxFontSize = this.getSmallValue(height, fontSize);

    const maxLength = new Decimal(width).div(fontSize).toNumber();
    const currentLength = this.strLength(text);
    if (currentLength <= maxLength) {
      // 変更の必要なし
      return this.getSmallValue(fontSize, maxFontSize);
    }
    const nextFontSize = this.getAutoFontSizeTextBox(width, height, new Decimal(fontSize).sub(0.1).toNumber(), text);
    return this.getSmallValue(this.roundSpecificDecimalPlace(nextFontSize, 2, RoundKinds.HalfUp), maxFontSize);
  }

  private static getSmallValue(left: number, right: number): number {
    return (left > right) ? right : left;
  }

  private static getHeadText(text: string, maxLength: number): string {
    let sb = "";
    let textLength = 0;

    // 半角で0.5、全角で1を足す処理だと浮動小数点の誤差対策でmathを使わないといけないので、2倍して整数で処理して、最後に割っている
    // ※+=で書かないとtslintでワーニングが出る
    const limitValue = new Decimal(maxLength).mul(2).toNumber();

    for (let i = 0; i < text.length; i++) {
      const char = text.substring(i, i + 1);
      if (this.isHankaku(char)) {
        textLength += 1;
      } else {
        textLength += 2;
      }
      if (limitValue <= textLength) {
        break;
      }
      sb += char;
    }
    return sb;
  }

  private static strLength(text: string): number {
    let textLength = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.substring(i, i + 1);
      if (this.isHankaku(char)) {
        textLength += 1;
      } else {
        textLength += 2;
      }
    }
    // 半角で0.5、全角で1を足す処理だと浮動小数点の誤差対策でmathを使わないといけないので、2倍して整数で処理して、最後に割っている
    // ※+=で書かないとtslintでワーニングが出る
    return new Decimal(textLength).div(2).toNumber();
  }

  private static isHankaku(text: string): boolean {
    return !!text
      ? this.checkLength(text, false)
      : true;
  }

  private static checkLength(str: string, isZenkaku: boolean): boolean {
    for (let i = 0; i < str.length; i++) {
      const c = str.charCodeAt(i);
      // Shift_JIS: 0x0 ～ 0x80, 0xa0 , 0xa1 ～ 0xdf , 0xfd ～ 0xff
      // Unicode : 0x0 ～ 0x80, 0xf8f0, 0xff61 ～ 0xff9f, 0xf8f1 ～ 0xf8f3
      if ((c >= 0x0 && c < 0x81) || (c === 0xf8f0) || (c >= 0xff61 && c < 0xffa0) || (c >= 0xf8f1 && c < 0xf8f4)) {
        if (!isZenkaku) { return true; }
      } else {
        if (isZenkaku) { return true; }
      }
    }
    return false;
  }

  private static roundSpecificDecimalPlace(value: number, targetDecimalPlace: number, round: RoundKinds): number {
    if (targetDecimalPlace === 0) { return value; }
    const shiftValue = new Decimal(10).pow(targetDecimalPlace - 1).toNumber();
    const rounded = this.roundValue(new Decimal(value).mul(shiftValue).toNumber(), round);
    return new Decimal(rounded).div(shiftValue).toNumber();
  }

  private static roundValue(value: number, kind: RoundKinds): number {
    switch (kind) {
    case RoundKinds.HalfUp:
      return new Decimal(value).round().toNumber();
    case RoundKinds.Up:
      return new Decimal(value).ceil().toNumber();
    case RoundKinds.Down:
      return new Decimal(value).floor().toNumber();
    }
    throw new Error("not implemented");
  }

  /**
   * 入力エリアに収まるテキストと、はみ出すテキストに分割する
   * すべて入力エリアに収まるとき：長さ1の配列を返却
   * はみ出すとき：長さ2の配列を返却
   */
  public static splitExcessText(text: string, fontSize: number, lineHeight: number, width: number, height: number, isMultiLine: boolean): string[] {
    const lines = text.split("\n");
    const sourceObjectLines: string[] = [];
    const excessLines: string[] = [];
    for (const line of lines) {
      const displayLines = this.splitLineByWidth(line, fontSize, width);
      const lastDisplayLine = displayLines[displayLines.length - 1];
      displayLines[displayLines.length - 1] = lastDisplayLine + "\n";
      for (const displayLine of displayLines) {
        const sourceTotalLineHeight = new Decimal(sourceObjectLines.length + 1).mul(fontSize).mul(lineHeight).toNumber();
        if (sourceTotalLineHeight > height) {
          excessLines.push(displayLine);
        } else {
          sourceObjectLines.push(displayLine);
        }
      }
    }
    return (excessLines.length === 0)
      ? [this.removeLastNewLine(sourceObjectLines.join(""))]
      : [this.removeLastNewLine(sourceObjectLines.join("")), this.removeLastNewLine(excessLines.join(""))];
  }

  /**
   * 入力エリア右端で折り返されたときの見た目上の行に分割する
   */
  private static splitLineByWidth(text: string, fontSize: number, width: number): string[] {
    const maxCharLength = this.roundSpecificDecimalPlace(new Decimal(width).div(fontSize).toNumber(), 2, RoundKinds.Down);
    const lines: string[] = [];
    let currentLine = "";
    let currentLineLength = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.substring(i, i + 1);
      const charLength = this.isHankaku(char) ? 0.5 : 1;
      if (new Decimal(currentLineLength).add(charLength).toNumber() >= maxCharLength) {
        lines.push(currentLine);
        currentLine = char;
        currentLineLength = charLength;
      } else {
        currentLine += char;
        currentLineLength = new Decimal(currentLineLength).add(charLength).toNumber();
      }
    }
    lines.push(currentLine);
    return lines;
  }

  /**
   * 行の末尾の改行コードを削除する
   */
  private static removeLastNewLine(text: string): string {
    return text.replace(/\n$/g, "").replace(/\r$/g, "");
  }

  public static calculateTextHeight(width: number, fontSize: number, lineHeight: number, text: string): number {
    const displayLineCount = this.calculateDisplayLineCount(width, fontSize, text);
    return this.roundSpecificDecimalPlace(new Decimal(displayLineCount).mul(fontSize).mul(lineHeight).toNumber(), 1, RoundKinds.Up);
  }

  public static calculateObjectHeight(item: IHeightAutoAdjustable): number {
    if (!item.shouldAutoAdjustHeight) { return item.originalHeight; }
    const calculated = FontSizeCalculator.calculateTextHeight(item.geometry.rectangle.width, item.textStyle.displayFontSize, item.textStyle.lineHeight, item.text());
    return (calculated < item.originalHeight)
      ? item.originalHeight
      : calculated;
  }
}
